/**
 * What only exists in the copied country, placed here (v2, 26/10/2026; laid out 27/10): its
 * gateways, lists, list groups and campaigns as four kinds of rows — each row knowing whether it
 * needs the person (no match, or a near one nobody confirmed), how it matched, and how to draw
 * itself: full in the status card, compact in its kind's fold.
 * For Media Devoted | Created By Ciro Autiero
 */
import { ReactNode, useMemo } from 'react';
import { Layers, List, type LucideIcon, Megaphone, RadioTower } from 'lucide-react';
import type { GroupCopyChoices, GroupCopyMatch, GroupCopyReview } from '@trafficpulse/shared';

import { withCampaign, withClearedSenders, withGateway, withList, withListGroup } from '@/features/drop-generator/components/group-copy/dialog/model/paste-choices';
import { type MatchKind, needsPick } from '@/features/drop-generator/components/group-copy/dialog/model/paste-summary';
import { useCountry } from '@/hooks/smswiser/core/useSMSWiser';

import { CampaignPick } from './items/CampaignPick';
import { GatewayPick } from './items/GatewayPick';
import { ListGroupPick } from './items/ListGroupPick';
import { ListPick } from './items/ListPick';
import { matchBadge, type MatchBadgeSpec } from './MatchBadge';

export interface PickEntry {
  key: string;
  needs: boolean;
  badge: MatchBadgeSpec;
  render: (compact: boolean) => ReactNode;
}

export interface PickKind {
  kind: MatchKind;
  title: string;
  icon: LucideIcon;
  legend: string;
  entries: PickEntry[];
}

type On = (next: GroupCopyChoices) => void;
type Ctx = { review: GroupCopyReview; choices: GroupCopyChoices; on: On };

const has = (map: Record<string, unknown> | undefined, key: string) => key in (map ?? {});

const entry = (key: string, row: { how: GroupCopyMatch; target: unknown }, answered: boolean, render: PickEntry['render']): PickEntry => ({
  key,
  needs: needsPick(row.how, answered),
  badge: matchBadge(row.how, row.target !== null, answered),
  render,
});

const gateways = ({ review, choices, on }: Ctx): PickEntry[] =>
  review.gateways.map((g) => {
    const key = String(g.sourceRouteId);
    const answered = has(choices.gateways, key);
    return entry(key, g, answered, (compact) => (
      <GatewayPick key={key} compact={compact} row={g} options={review.options.gateways} answered={answered} onPick={(t) => on(withGateway(choices, g.sourceRouteId, t))} onClearSenders={(c) => on(withClearedSenders(choices, g.sourceRouteId, c))} />
    ));
  });

const lists = ({ review, choices, on }: Ctx, options: Array<{ value: string; label: string }>): PickEntry[] =>
  (review.lists ?? []).map((l) => {
    const key = String(l.sourceWiserId);
    const answered = has(choices.lists, key);
    return entry(key, l, answered, (compact) => <ListPick key={key} compact={compact} row={l} options={options} answered={answered} onPick={(t) => on(withList(choices, l.sourceWiserId, t))} />);
  });

const listGroups = ({ review, choices, on }: Ctx): PickEntry[] =>
  review.listGroups.map((l) => {
    const answered = has(choices.listGroups, l.sourceId);
    return entry(l.sourceId, l, answered, (compact) => <ListGroupPick key={l.sourceId} compact={compact} row={l} options={review.options.listGroups} answered={answered} onPick={(t) => on(withListGroup(choices, l.sourceId, t))} />);
  });

const campaigns = ({ review, choices, on }: Ctx): PickEntry[] =>
  review.campaigns.map((c) => {
    const key = String(c.sourceWiserId);
    const answered = has(choices.campaigns, key);
    return entry(key, c, answered, (compact) => <CampaignPick key={key} compact={compact} row={c} options={review.options.campaigns} answered={answered} onPick={(t) => on(withCampaign(choices, c.sourceWiserId, t))} />);
  });

const LEGEND: Record<MatchKind, string> = {
  gateways: 'Each gateway lands on the route of the same SMSWiser gateway here (the same in every country), else one with the same name. Its sender IDs come along unless you clear them; one left out leaves its pool lanes and drop pins behind.',
  lists: 'Each list lands on the list with its name here, the country prefix left out (AU_… → CZ_…); a near name is used — confirm it. One left out leaves its drop pins and "my order" place behind.',
  listGroups: 'A shared group of this country with the same name, else the group created here — its rule as this country reads it (hand-picked lists on their matches).',
  campaigns: 'Each container runs on one campaign of the target group — the one with its name, else the group’s only one.',
};

function useListOptions(countryKey: string) {
  const leads = useCountry(countryKey).data?.leadsLists;
  return useMemo(() => (leads ?? []).map((l) => ({ value: String(l.id), label: l.name })), [leads]);
}

export function usePickKinds(review: GroupCopyReview, choices: GroupCopyChoices, on: On): PickKind[] {
  const listOptions = useListOptions(review.target.countryKey);
  const ctx = { review, choices, on };
  const kind = (k: MatchKind, title: string, icon: LucideIcon, entries: PickEntry[]): PickKind => ({ kind: k, title, icon, legend: LEGEND[k], entries });
  return [
    kind('gateways', 'Gateways', RadioTower, gateways(ctx)),
    kind('lists', 'Lists', List, lists(ctx, listOptions)),
    kind('listGroups', 'List groups', Layers, listGroups(ctx)),
    kind('campaigns', 'Campaigns', Megaphone, campaigns(ctx)),
  ];
}
